import { useLocation } from "react-router-dom";
import { Maximize, Minimize, Video } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { secureRandom } from "../../../components/streaming/utils/secureRandom";
import RecordingPlayer from "../../../components/SecureVideoPlayer";

const VIDEO_PLAYER_CSS = `
  video {
    width: 100%;
    height: 100%;
    display: block;
    background: black;
  }

  @media (max-width: 768px) {
    .video-container {
      height: 100% !important;
      overflow: hidden;
    }
    video {
      object-fit: contain;
    }
  }

  @media (min-width: 769px) {
    .video-container {
      height: 100%;
      overflow: visible;
    }
  }

  video::-webkit-media-controls-fullscreen-button {
    display: none !important;
  }
`;

interface StreamViewRecordingProps {
  videoUrl?: string;
  user?: any;
  cnr?: string;
  hearingDate?: string;
}

const StreamViewRecording = (props: StreamViewRecordingProps = {}) => {
  const location = useLocation();
  const routeState = location.state || {};
  const videoUrl = props.videoUrl || routeState.videoUrl;
  const user = props.user || routeState.user;
  const cnr = props.cnr || routeState.cnr;
  const hearingDate = props.hearingDate || routeState.hearingDate;

  const containerRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pulse, setPulse] = useState(0.4);
  const [randPos, setRandPos] = useState({ top: 10, left: 10 });
  const [timestamp, setTimestamp] = useState(Date.now());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hideCursor, setHideCursor] = useState(false);

  

  // Fullscreen state sync
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // Watermark animation
  useEffect(() => {
    const pos = setInterval(() => {
      setRandPos({
        top: secureRandom() * 80 + 5,
        left: secureRandom() * 80 + 5,
      });
    }, 2500);

    const pulseI = setInterval(() => {
      setPulse((p) => (p === 0.4 ? 0.2 : 0.4));
    }, 1500);

    const timeI = setInterval(() => setTimestamp(Date.now()), 1000);

    return () => {
      clearInterval(pos);
      clearInterval(pulseI);
      clearInterval(timeI);
    };
  }, []);

  // Cleanup hide timer on unmount
  useEffect(() => {
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  const handleMouseMove = () => {
    setShowControls(true);
    setHideCursor(false);

    if (hideTimer.current) clearTimeout(hideTimer.current);

    hideTimer.current = setTimeout(() => {
      setShowControls(false);
      setHideCursor(true);
    }, 2000);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;

    if (!document.fullscreenElement) el.requestFullscreen();
    else document.exitFullscreen();
  };

  const watermarkText = user
    ? `${user.name} | ${user.email} | ${user.phone} | ${user.ip} | ${new Date(timestamp).toLocaleString()}`
    : "";

  // No videoUrl — guard
  if (!videoUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black">
        <Video size={40} className="text-red-600" />
        <p className="text-white ml-3 text-sm">No video available</p>
      </div>
    );
  }

  return (
    <div className="relative w-full bg-black h-full">
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onTouchStart={() => setShowControls(true)}
        className={`video-container relative w-full h-full bg-black group ${
          hideCursor ? "cursor-none" : "cursor-default"
        }`}
      >
        {/* ── Video Player — src passed as prop ── */}
        <RecordingPlayer src={videoUrl} cnr={cnr} hearingDate={hearingDate} />

        <style>{VIDEO_PLAYER_CSS}</style>

        {/* Fullscreen Button */}
        <button
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          className={`absolute bottom-2.5 right-6 bg-black/70 text-white px-4 py-2 rounded-lg z-[999999] transition-opacity duration-300 ${
            showControls ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
        </button>

        {/* Watermark */}
        {user && watermarkText && (
          <div
            className="absolute z-[9999] pointer-events-none hidden sm:block select-none"
            style={{
              top: `${randPos.top}%`,
              left: `${randPos.left}%`,
              opacity: pulse,
              color: "white",
              fontSize: "16px",
              whiteSpace: "normal",
              maxWidth: "90%",
              textShadow: "1px 1px 3px rgba(0,0,0,0.9)",
              transition: "opacity 0.5s ease",
            }}
          >
            {watermarkText}
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamViewRecording;
